import React from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaSection from '../components/CtaSection';
import ScrollReveal from '../components/animated/ScrollReveal';
import Text from '../components/Text';
import { colors } from '../theme/designTokens';
import { industries, getIndustryBySlug } from '../data/industries';

export const IndustriesHub = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />
      <PageHero
        variant="dark"
        eyebrow="Industries"
        title="Sector resilience."
        titleHighlight="By design."
        subtitle="Each industry faces a distinct disruption profile. ALEgION designs resilience architectures that fit the operating reality of your sector."
        badges={['Manufacturing', 'Healthcare', 'BFSI', 'Technology']}
      />
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <ScrollReveal>
            <SectionHeader
              eyebrow="Sectors"
              title="Where we engineer resilience."
              highlightWords={['engineer']}
            />
          </ScrollReveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {industries.map((ind) => (
              <Box
                key={ind.slug}
                component="button"
                type="button"
                onClick={() => navigate(`/industries/${ind.slug}`)}
                sx={{
                  textAlign: 'left',
                  p: 3,
                  cursor: 'pointer',
                  border: `1px solid ${colors.borderLight}`,
                  background: colors.white,
                  minHeight: 160,
                  '&:hover': { borderColor: colors.navy },
                }}
              >
                <Text variant="h4" color="primary" sx={{ mb: 1.5 }}>
                  {ind.title}
                </Text>
                <Text variant="bodySm" color="secondary">
                  {ind.summary}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <CtaSection />
      <Footer />
    </Box>
  );
};

export const IndustryDetail = () => {
  const { slug } = useParams();
  const ind = getIndustryBySlug(slug);
  if (!ind) return <Navigate to="/industries" replace />;

  return (
    <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
      <NavBar />
      <PageHero
        variant="dark"
        eyebrow="Industry"
        title={ind.title}
        subtitle={ind.summary}
        badges={['Sector Resilience', 'AERA™']}
        minHeight={{ xs: 300, md: 400 }}
      />
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 10 } }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <ScrollReveal>
            <Text variant="h3" color="primary" sx={{ mb: 2 }}>
              Resilience priorities for {ind.title}
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block' }}>
              {ind.summary} Through AERA™, we help {ind.title.toLowerCase()} organizations anticipate sector-specific threats, assess maturity and exposure, architect integrated protection, activate response when disruption hits, and advance continuously through monitoring and learning.
            </Text>
            <Text variant="body" color="secondary" sx={{ mb: 4, display: 'block' }}>
              Insurance remains a critical capability—but it is presented as one component within a broader resilience architecture tailored to how this sector operates, fails, and recovers.
            </Text>
            <Box
              component={RouterLink}
              to="/industries"
              sx={{ color: colors.navy, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              ← All Industries
            </Box>
          </ScrollReveal>
        </Box>
      </Box>
      <CtaSection />
      <Footer />
    </Box>
  );
};

export default IndustriesHub;
